import { useContext, useState, useEffect } from 'react';

import {
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem,
  Collapse,
  ListItemButton
} from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';

import DesignServicesTwoToneIcon from '@mui/icons-material/DesignServicesTwoTone';
import BrightnessLowTwoToneIcon from '@mui/icons-material/BrightnessLowTwoTone';
import MmsTwoToneIcon from '@mui/icons-material/MmsTwoTone';
import TableChartTwoToneIcon from '@mui/icons-material/TableChartTwoTone';
import AccountCircleTwoToneIcon from '@mui/icons-material/AccountCircleTwoTone';
import BallotTwoToneIcon from '@mui/icons-material/BallotTwoTone';
import BeachAccessTwoToneIcon from '@mui/icons-material/BeachAccessTwoTone';
import EmojiEventsTwoToneIcon from '@mui/icons-material/EmojiEventsTwoTone';
import FilterVintageTwoToneIcon from '@mui/icons-material/FilterVintageTwoTone';
import HowToVoteTwoToneIcon from '@mui/icons-material/HowToVoteTwoTone';
import LocalPharmacyTwoToneIcon from '@mui/icons-material/LocalPharmacyTwoTone';
import RedeemTwoToneIcon from '@mui/icons-material/RedeemTwoTone';
import SettingsTwoToneIcon from '@mui/icons-material/SettingsTwoTone';
import TrafficTwoToneIcon from '@mui/icons-material/TrafficTwoTone';
import CheckBoxTwoToneIcon from '@mui/icons-material/CheckBoxTwoTone';
import ChromeReaderModeTwoToneIcon from '@mui/icons-material/ChromeReaderModeTwoTone';
import WorkspacePremiumTwoToneIcon from '@mui/icons-material/WorkspacePremiumTwoTone';
import CameraFrontTwoToneIcon from '@mui/icons-material/CameraFrontTwoTone';
import DisplaySettingsTwoToneIcon from '@mui/icons-material/DisplaySettingsTwoTone';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DashboardTwoToneIcon from '@mui/icons-material/DashboardTwoTone';
import AdminPanelSettingsTwoToneIcon from '@mui/icons-material/AdminPanelSettingsTwoTone';
import PeopleTwoToneIcon from '@mui/icons-material/PeopleTwoTone';
import ExtensionTwoToneIcon from '@mui/icons-material/ExtensionTwoTone';
import DescriptionTwoToneIcon from '@mui/icons-material/DescriptionTwoTone';
import PsychologyTwoToneIcon from '@mui/icons-material/PsychologyTwoTone';
import FactoryTwoToneIcon from '@mui/icons-material/FactoryTwoTone';
import SchoolTwoToneIcon from '@mui/icons-material/SchoolTwoTone';
import ForumTwoToneIcon from '@mui/icons-material/ForumTwoTone';
import TrendingUpTwoToneIcon from '@mui/icons-material/TrendingUpTwoTone';
import AutoStoriesTwoToneIcon from '@mui/icons-material/AutoStoriesTwoTone';
import AutoAwesomeTwoToneIcon from '@mui/icons-material/AutoAwesomeTwoTone';
import AssessmentTwoToneIcon from '@mui/icons-material/AssessmentTwoTone';
import StyleTwoToneIcon from '@mui/icons-material/StyleTwoTone';



const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListItemButton-root {
      text-transform: uppercase;
      font-weight: 900;
      letter-spacing: 0.1em;
      font-size: ${theme.typography.pxToRem(11)};
      color: ${theme.palette.text.secondary};
      padding: ${theme.spacing(0.5, 1.5)};
      line-height: 1.4;
      border-radius: ${theme.spacing(1)};
      margin-bottom: 0;
      
      &:hover {
        background-color: transparent;
        color: ${theme.palette.text.primary};
      }

      .MuiSvgIcon-root {
        font-size: ${theme.typography.pxToRem(20)};
        margin-right: ${theme.spacing(1)};
        color: inherit;
      }
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {
      .MuiListItem-root {
        padding: 1px 0;

        .MuiButton-root {
          display: flex;
          color: ${theme.palette.text.primary};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(0.5, 1.5)};
          border-radius: ${theme.spacing(1)};
          text-transform: none;
          font-weight: 500;
          margin-bottom: 0;

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: inherit;
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: inherit;
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.palette.primary.main, 0.1)};
            color: ${theme.palette.primary.main};
            font-weight: bold;

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.palette.primary.main};
            }
          }
        }
      }
    }
`
);


import { useTranslation } from 'react-i18next';
import useAuthStore from 'src/store/useAuthStore';
import CorporateFareTwoToneIcon from '@mui/icons-material/CorporateFareTwoTone';
import WorkTwoToneIcon from '@mui/icons-material/WorkTwoTone';

function SidebarMenu() {
  const { closeSidebar } = useContext(SidebarContext);
  const { t } = useTranslation();
  const { user } = useAuthStore();

  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem('sidebar_open_states');
    return saved
      ? JSON.parse(saved)
      : {
        dashboards: false,
        management: false,
        accounts: false,
        components: false,
        extraPages: false,
        trainings: false
      };

  });

  const handleToggle = (section: string) => {
    setOpen((prev) => {
      const newState = { ...prev, [section]: !prev[section] };
      localStorage.setItem('sidebar_open_states', JSON.stringify(newState));
      return newState;
    });
  };

  const isAdmin = user?.role === 'super_admin' || user?.role === 'tenant_admin' || user?.role === 'unit_manager';

  const trainingItems = (
    <>
      {isAdmin && (
        <ListItem component="div">
          <Button
            disableRipple
            component={RouterLink}
            onClick={closeSidebar}
            to="/trainings/content-factory"
            startIcon={<FactoryTwoToneIcon />}
          >
            {t('header.content_factory', 'Content Factory')}
          </Button>
        </ListItem>
      )}
      {isAdmin && (
        <ListItem component="div">
          <Button
            disableRipple
            component={RouterLink}
            onClick={closeSidebar}
            to="/trainings/micro-lessons"
            startIcon={<MmsTwoToneIcon />}
          >
            {t('header.micro_lessons', 'Mikro Dersler')}
          </Button>
        </ListItem>
      )}
      <ListItem component="div">
        <Button
          disableRipple
          component={RouterLink}
          onClick={closeSidebar}
          to="/trainings/micro-lessons-player"
          startIcon={<MmsTwoToneIcon />}
        >
          {t('header.micro_lessons', 'Mikro Dersler')}
        </Button>
      </ListItem>
      <ListItem component="div">
        <Button
          disableRipple
          component={RouterLink}
          onClick={closeSidebar}
          to="/trainings/roadmap"
          startIcon={<TrendingUpTwoToneIcon />}
        >
          {t('header.roadmap', 'Roadmap')}
        </Button>
      </ListItem>
      <ListItem component="div">
        <Button
          disableRipple
          component={RouterLink}
          onClick={closeSidebar}
          to="/trainings/flashcards"
          startIcon={<StyleTwoToneIcon />}
        >
          {t('header.flashcards', 'Flashcards')}
        </Button>
      </ListItem>
      <ListItem component="div">
        <Button
          disableRipple
          component={RouterLink}
          onClick={closeSidebar}
          to="/trainings/daily-refresh"
          startIcon={<SchoolTwoToneIcon />}
        >
          {t('header.daily_refresh', 'Daily Refresh')}
        </Button>
      </ListItem>
      <ListItem component="div">
        <Button
          disableRipple
          component={RouterLink}
          onClick={closeSidebar}
          to="/trainings/reflection"
          startIcon={<PsychologyTwoToneIcon />}
        >
          {t('header.reflection_coach', 'Reflection Coach')}
        </Button>
      </ListItem>
      <ListItem component="div">
        <Button
          disableRipple
          component={RouterLink}
          onClick={closeSidebar}
          to="/trainings/oracle"
          startIcon={<AutoStoriesTwoToneIcon />}
        >
          {t('header.oracle', 'The Oracle')}
        </Button>
      </ListItem>
      <ListItem component="div">
        <Button
          disableRipple
          component={RouterLink}
          onClick={closeSidebar}
          to="/trainings/simulations"
          startIcon={<ForumTwoToneIcon />}
        >
          {t('header.simulations', 'Active Simulations')}
        </Button>
      </ListItem>
      <ListItem component="div">
        <Button
          disableRipple
          component={RouterLink}
          onClick={closeSidebar}
          to="/trainings/analytics-dashboard"
          startIcon={<DashboardTwoToneIcon />}
        >
          {t('header.analytics_dashboard', 'Analytics Dashboard')}
        </Button>
      </ListItem>
      <ListItem component="div">
        <Button
          disableRipple
          component={RouterLink}
          onClick={closeSidebar}
          to={user?.id ? `/management/users/report/${user.id}` : '#'}
          startIcon={<AssessmentTwoToneIcon />}
        >
          {t('header.my_report', 'Raporum')}
        </Button>
      </ListItem>

      {user?.role === 'super_admin' && (
        <ListItem component="div">
          <Button
            disableRipple
            component={RouterLink}
            onClick={closeSidebar}
            to="/trainings/lms-clone"
            startIcon={<AutoAwesomeTwoToneIcon />}
          >
            {t('header.lms_template', 'LMS Template')}
          </Button>
        </ListItem>
      )}
    </>
  );

  return (
    <>
      <MenuWrapper>
        <List component="div">
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/overview"
                  startIcon={<DesignServicesTwoToneIcon />}
                >
                  {t('header.overview', 'Overview')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>

        {isAdmin && (
          <List component="div">
            <ListItemButton
              onClick={() => handleToggle('management')}
              disableRipple
            >
              <AdminPanelSettingsTwoToneIcon />
              {t('header.management', 'Management')}
              {open.management ? (
                <ExpandLess sx={{ ml: 'auto' }} />
              ) : (
                <ExpandMore sx={{ ml: 'auto' }} />
              )}
            </ListItemButton>
            <Collapse in={open.management} timeout="auto" unmountOnExit>
              <SubMenuWrapper>
                <List component="div">
                  {user?.role === 'super_admin' && (
                    <ListItem component="div">
                      <Button
                        disableRipple
                        component={RouterLink}
                        onClick={closeSidebar}
                        to="/management/tenants"
                        startIcon={<CorporateFareTwoToneIcon />}
                      >
                        {t('header.tenants', 'Tenants')}
                      </Button>
                    </ListItem>
                  )}
                  {isAdmin && (
                      <ListItem component="div">
                        <Button
                          disableRipple
                          component={RouterLink}
                          onClick={closeSidebar}
                          to="/management/org-units"
                          startIcon={<TableChartTwoToneIcon />}
                        >
                          {t('header.branches', 'Branches')}
                        </Button>
                      </ListItem>
                    )}
                  {isAdmin && (
                      <ListItem component="div">
                        <Button
                          disableRipple
                          component={RouterLink}
                          onClick={closeSidebar}
                          to="/management/job-titles"
                          startIcon={<WorkTwoToneIcon />}
                        >
                          {t('header.job_titles', 'Job Titles')}
                        </Button>
                      </ListItem>
                    )}
                  {user?.role === 'super_admin' && (
                    <>
                      <ListItem component="div">
                        <Button
                          disableRipple
                          component={RouterLink}
                          onClick={closeSidebar}
                          to="/management/system-settings"
                          startIcon={<SettingsTwoToneIcon />}
                        >
                          {t('header.system_settings', 'System Settings')}
                        </Button>
                      </ListItem>
                      <ListItem component="div">
                        <Button
                          disableRipple
                          component={RouterLink}
                          onClick={closeSidebar}
                          to="/management/prompts"
                          startIcon={<ExtensionTwoToneIcon />}
                        >
                          {t('header.prompts', 'Prompt Management')}
                        </Button>
                      </ListItem>
                    </>
                  )}
                </List>
              </SubMenuWrapper>
            </Collapse>
          </List>
        )}
        
        {!isAdmin ? (
          <List component="div">
            <SubMenuWrapper>
              <List component="div">
                {trainingItems}
              </List>
            </SubMenuWrapper>
          </List>
        ) : (
          <List component="div">
            <ListItemButton
              onClick={() => handleToggle('trainings')}
              disableRipple
            >
              <PsychologyTwoToneIcon />
              {t('header.trainings', 'Trainings')}
              {open.trainings ? (
                <ExpandLess sx={{ ml: 'auto' }} />
              ) : (
                <ExpandMore sx={{ ml: 'auto' }} />
              )}
            </ListItemButton>
            <Collapse in={open.trainings} timeout="auto" unmountOnExit>
              <SubMenuWrapper>
                <List component="div">
                  {trainingItems}
                </List>
              </SubMenuWrapper>
            </Collapse>
          </List>
        )}

        {(user?.role === 'super_admin' ||
          user?.role === 'tenant_admin' ||
          user?.role === 'unit_manager') && (
          <List component="div">
            <ListItemButton
              onClick={() => handleToggle('accounts')}
              disableRipple
            >
              <PeopleTwoToneIcon />
              {t('header.accounts', 'Accounts')}
              {open.accounts ? (
                <ExpandLess sx={{ ml: 'auto' }} />
              ) : (
                <ExpandMore sx={{ ml: 'auto' }} />
              )}
            </ListItemButton>
            <Collapse in={open.accounts} timeout="auto" unmountOnExit>
              <SubMenuWrapper>
                <List component="div">
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/management/users"
                      startIcon={<AccountCircleTwoToneIcon />}
                    >
                      {t('header.users', 'Users')}
                    </Button>
                  </ListItem>
                </List>
              </SubMenuWrapper>
            </Collapse>
          </List>
        )}
      </MenuWrapper>
    </>
  );
}

export default SidebarMenu;
